import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Logo } from "@/components/Logo";
import { ComparisonTable } from "@/components/ComparisonTable";
import { ArrowRight, BarChart3, FileText, Share2, TrendingUp, Zap, Shield, Home, Mail, BarChart, Code2, Webhook, BadgeCheck, Check, Sparkles, Eye, Globe, Palette, ListTodo } from "lucide-react";
import { SpotlightIconPattern } from "@/components/spotlight/SpotlightIconPattern";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";

import { ChristmasEffect } from "@/components/ChristmasEffect";
import { usePlatformSettingBoolean } from "@/hooks/usePlatformSetting";
import kristiJencks from "@/assets/kristi-jencks.png";

export default function Landing() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [billingFrequency, setBillingFrequency] = useState<'monthly' | 'annual'>('monthly');
  const { enabled: christmasThemeEnabled, isLoading: isLoadingTheme } = usePlatformSettingBoolean('christmas_theme_enabled', false);
  
  // Debug log
  useEffect(() => {
    console.log('Christmas theme enabled:', christmasThemeEnabled, 'isLoading:', isLoadingTheme);
  }, [christmasThemeEnabled, isLoadingTheme]);

  // Plan pricing configuration
  const planPricing = {
    starter: { monthly: 67, annual: 737 },
    lite: { monthly: 99, annual: 1089 },
    growth: { monthly: 149, annual: 1639 },
    scale: { monthly: 249, annual: 2739 },
  };

  useEffect(() => {
    // Check if user is already logged in
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session);
    });

    // Fetch the logo
    const fetchLogo = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('north_logo_url')
        .not('north_logo_url', 'is', null)
        .limit(1)
        .maybeSingle();
      
      if (data?.north_logo_url) {
        setLogoUrl(data.north_logo_url);
      }
    };
    fetchLogo();
  }, []);

  return (
    <div className="min-h-screen bg-background relative">
      {/* Christmas Effects - Only show if enabled and not loading */}
      {!isLoadingTheme && christmasThemeEnabled && (
        <ChristmasEffect />
      )}
      
      {/* Header */}
      <header className="border-b border-border bg-card/95 backdrop-blur-md sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <Logo customLogoUrl={logoUrl} size="sm" />
            <div className="flex items-center gap-3">
              <Button variant="ghost" asChild className="hidden sm:inline-flex">
                <a href="#pricing">Pricing</a>
              </Button>
              {isAuthenticated ? (
                <Button className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 hover:from-indigo-500 hover:via-purple-500 hover:to-pink-400 border-0 text-white" asChild>
                  <Link to="/dashboard">Report with North</Link>
                </Button>
              ) : (
                <>
                  <Button variant="ghost" asChild>
                    <Link to="/login">Sign In</Link>
                  </Button>
                  <Button className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 hover:from-indigo-500 hover:via-purple-500 hover:to-pink-400 border-0 text-white" asChild>
                    <Link to="/signup">Get Started</Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative isolate overflow-hidden bg-white">
        {/* Subtle top-right gradient wash */}
        <div className="absolute inset-y-0 right-0 -z-10 w-full">
          <div
            className="absolute inset-0 animate-gradient-shift"
            style={{
              backgroundSize: "200% 200%",
              backgroundImage: "linear-gradient(135deg, transparent 0%, transparent 30%, #c7d2fe40 50%, #818cf840 65%, #6366f130 80%, transparent 100%)",
            }}
          />
        </div>

        <div className="mx-auto max-w-7xl pt-10 pb-24 sm:pb-32 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:px-8 lg:py-40">
          {/* Left column — Copy */}
          <div className="px-6 lg:px-0 lg:pt-4">
            <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-lg animate-fade-in">
              <Badge className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-50 border-indigo-200/50 text-indigo-600 hover:bg-indigo-100">
                <Zap className="h-4 w-4" />
                Real Estate Marketing Intelligence
              </Badge>

              <h1 className="mt-10 text-5xl font-semibold tracking-tight text-gray-900 sm:text-7xl leading-[1.1]">
                Elevate Your Listing
                <span className="block mt-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent">
                  With Data-Driven Reports
                </span>
              </h1>

              <p className="mt-8 text-lg text-gray-500 sm:text-xl/8">
                Transform your listings into comprehensive seller reports that showcase market insights,
                property analytics, and social media performance.
              </p>

              <div className="mt-10 flex items-center gap-x-6">
                <Button size="lg" className="text-lg gap-2 shadow-lg hover:shadow-xl transition-all bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 hover:from-indigo-500 hover:via-purple-500 hover:to-pink-400 border-0 text-white" asChild>
                  <Link to={isAuthenticated ? "/dashboard" : "/signup"}>
                    {isAuthenticated ? "Go to Dashboard" : "Start Free Trial"}
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>
                <a href="#features" className="text-sm font-semibold text-gray-900 hover:text-gray-600 transition-colors">
                  See features <span aria-hidden="true">→</span>
                </a>
              </div>
            </div>
          </div>

          {/* Right column — Demo preview, extends to viewport edge */}
          <div className="mt-20 sm:mt-24 md:mx-auto md:max-w-2xl lg:mx-0 lg:mt-0 lg:w-screen">
            {/* Skewed white divider panel */}
            <div aria-hidden="true" className="absolute inset-y-0 right-1/2 -z-10 -mr-10 w-[200%] skew-x-[-30deg] bg-white shadow-xl shadow-indigo-600/10 ring-1 ring-indigo-50 md:-mr-20 lg:-mr-36" />
            <div
              className="shadow-lg rounded-l-3xl pl-8 pt-8 sm:pl-12 sm:pt-12 w-screen animate-gradient-shift"
              style={{
                backgroundSize: "200% 200%",
                backgroundImage: "linear-gradient(135deg, #6366f1 0%, #818cf8 25%, #6366f1 50%, #4f46e5 75%, #6366f1 100%)",
              }}
            >
              {/* Inner skew accent */}
              <div aria-hidden="true" className="absolute -inset-y-px left-1/2 -z-10 ml-10 w-[200%] skew-x-[-30deg] bg-indigo-100 opacity-20 ring-1 ring-inset ring-white md:ml-20 lg:ml-36" />
              <div className="w-screen overflow-hidden rounded-tl-xl bg-white">
                {/* Browser chrome */}
                <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-300" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-300" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-300" />
                  </div>
                  <div className="flex-1 mx-12">
                    <div className="bg-gray-100 rounded-full px-4 py-1 text-xs text-gray-400 text-center font-mono">
                      northreports.com/north/demo-report
                    </div>
                  </div>
                </div>
                {/* North Report demo iframe */}
                <div className="overflow-hidden" style={{ height: "600px" }}>
                  <iframe
                    src="/demo"
                    className="border-0 pointer-events-none"
                    style={{ width: "1400px", height: "900px", transform: "scale(0.85)", transformOrigin: "top left" }}
                    title="North Report Demo Preview"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom fade */}
        <div className="absolute inset-x-0 bottom-0 -z-10 h-24 bg-gradient-to-t from-white sm:h-32" />
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-gray-50/50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-20">
              <Badge className="mb-4 bg-indigo-50 text-indigo-600 border-indigo-200/50">
                Powerful Features
              </Badge>
              <h2 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
                Everything You Need to
                <span className="block mt-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent">Win More Listings</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                North transforms your listing data into professional seller reports that showcase your market expertise and prove your value.
              </p>
            </div>

            {/* Bento Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* 1. One-Click Reports — Hero card (2 col × 2 row) */}
              <div className="md:col-span-2 md:row-span-2 bg-white rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow relative overflow-hidden">
                <div className="h-1.5 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500" />
                <SpotlightIconPattern color="#6366f1" opacity={0.06} />
                <div className="relative p-8 sm:p-10 flex flex-col h-full">
                  <div className="h-16 w-16 rounded-2xl bg-indigo-100 flex items-center justify-center mb-6">
                    <Zap className="h-8 w-8 text-indigo-600" />
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">One-Click Reports</h3>
                  <p className="text-gray-500 text-lg leading-relaxed">
                    Paste any listing URL and instantly generate a professional seller report with market data, comps, portal analytics, and AI-powered insights.
                  </p>
                </div>
              </div>

              {/* 2. AI Agent Insights */}
              <div className="bg-white rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow p-8">
                <div className="h-12 w-12 rounded-xl bg-purple-100 flex items-center justify-center mb-4">
                  <Sparkles className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">AI Agent Insights</h3>
                <p className="text-gray-500 leading-relaxed">
                  Get intelligent recommendations on pricing strategy, marketing tactics, and next steps—powered by AI.
                </p>
              </div>

              {/* 3. Multi-Portal Tracking */}
              <div className="bg-white rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow p-8">
                <div className="h-12 w-12 rounded-xl bg-pink-100 flex items-center justify-center mb-4">
                  <Globe className="h-6 w-6 text-pink-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Multi-Portal Tracking</h3>
                <p className="text-gray-500 leading-relaxed">
                  Track views and saves across Zillow, Realtor.com, Homes.com, Redfin, and Trulia in one place.
                </p>
              </div>

              {/* 4. Spotlight Pages (2 col wide) */}
              <div className="md:col-span-2 bg-white rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow p-8">
                <div className="h-12 w-12 rounded-xl bg-indigo-100 flex items-center justify-center mb-4">
                  <Eye className="h-6 w-6 text-indigo-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Spotlight Pages</h3>
                <p className="text-gray-500 leading-relaxed">
                  Transform your sales history, market stats, and client reviews into a polished marketing page for every listing appointment.
                </p>
              </div>

              {/* 5. Email & Share */}
              <div className="bg-white rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow p-8">
                <div className="h-12 w-12 rounded-xl bg-purple-100 flex items-center justify-center mb-4">
                  <Mail className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Email & Share</h3>
                <p className="text-gray-500 leading-relaxed">
                  Generate shareable links and send reports directly to clients. Mobile-optimized for any device.
                </p>
              </div>

              {/* 6. Smart Comps */}
              <div className="bg-white rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow p-8">
                <div className="h-12 w-12 rounded-xl bg-pink-100 flex items-center justify-center mb-4">
                  <Home className="h-6 w-6 text-pink-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Smart Comps</h3>
                <p className="text-gray-500 leading-relaxed">
                  Automatically pull and analyze comparable properties with pricing trends and market positioning.
                </p>
              </div>

              {/* 7. Task Management */}
              <div className="bg-white rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow p-8">
                <div className="h-12 w-12 rounded-xl bg-indigo-100 flex items-center justify-center mb-4">
                  <ListTodo className="h-6 w-6 text-indigo-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Task Management</h3>
                <p className="text-gray-500 leading-relaxed">
                  Track listing tasks, deadlines, and action items to keep every deal moving forward.
                </p>
              </div>

              {/* 8. Full Brand Control */}
              <div className="bg-white rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow p-8">
                <div className="h-12 w-12 rounded-xl bg-purple-100 flex items-center justify-center mb-4">
                  <Palette className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Full Brand Control</h3>
                <p className="text-gray-500 leading-relaxed">
                  Custom logos, brand colors, and agent headshots make every report uniquely yours.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Report Preview Section */}
      <section className="w-full py-20 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white/20 border-white/30 text-white hover:bg-white/30">
              <FileText className="h-4 w-4" />
              See it in Action
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 mt-8">
              Professional Reports, Instantly
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Your North Reports look stunning on every device—from desktop presentations to mobile sharing with clients.
            </p>
          </div>

          <div className="grid lg:grid-cols-[65fr_35fr] gap-4 items-start max-w-7xl mx-auto">
            {/* Desktop Preview */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                  <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <rect x="2" y="3" width="20" height="14" rx="2" strokeWidth="2"/>
                    <path d="M8 21h8M12 17v4" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-white">Desktop Experience</h3>
              </div>
              <Card className="border-2 border-indigo-300/30 shadow-xl overflow-hidden bg-card">
                <div className="bg-muted/50 px-4 py-2 flex items-center gap-2 border-b">
                  <div className="flex gap-1.5">
                    <div className="h-3 w-3 rounded-full bg-red-400/60"></div>
                    <div className="h-3 w-3 rounded-full bg-yellow-400/60"></div>
                    <div className="h-3 w-3 rounded-full bg-green-400/60"></div>
                  </div>
                  <div className="flex-1 bg-background/60 rounded px-3 py-1 text-xs text-muted-foreground font-mono">
                    northreports.com/north/your-listing
                  </div>
                </div>
                <div className="relative w-full h-[600px] bg-background overflow-hidden">
                  <div className="w-full h-full" style={{
                    transform: 'scale(0.56)',
                    transformOrigin: 'top left',
                    width: '178.57%',
                    height: '178.57%'
                  }}>
                    <iframe
                      src="/demo"
                      className="w-full h-full border-0"
                      title="Desktop Preview"
                    />
                  </div>
                </div>
              </Card>
              <Button className="w-full gap-2 bg-white/10 text-white border border-white/30 hover:bg-white/20" asChild>
                <a href="/demo" target="_blank" rel="noopener noreferrer">
                  <ArrowRight className="h-4 w-4" />
                  View Live Demo Report
                </a>
              </Button>
            </div>

            {/* Mobile Preview */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                  <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <rect x="5" y="2" width="14" height="20" rx="2" strokeWidth="2"/>
                    <path d="M12 18h.01" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-white">Mobile Optimized</h3>
              </div>
              <div className="flex justify-center">
                <Card className="border-2 border-indigo-300/30 shadow-xl overflow-hidden bg-card w-full">
                  <div className="bg-muted/50 px-3 py-2 border-b">
                    <div className="flex-1 bg-background/60 rounded px-2 py-1 text-xs text-muted-foreground font-mono text-center">
                      northreports.com
                    </div>
                  </div>
                  <div className="relative h-[600px] bg-background overflow-hidden">
                    <div className="w-full h-full" style={{
                      transform: 'scale(0.7)',
                      transformOrigin: 'top center',
                      width: '142.86%',
                      height: '142.86%',
                      marginLeft: '-21.43%'
                    }}>
                      <iframe
                        src="/demo"
                        className="w-full h-full border-0"
                        title="Mobile Preview"
                      />
                    </div>
                  </div>
                </Card>
              </div>
              <p className="text-sm text-white/80 text-center max-w-sm mx-auto">
                Share reports instantly via text or email. Clients can view on any device, anywhere.
              </p>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* Testimonial / Social Proof */}
      <section className="pt-24 pb-12 bg-gradient-to-b from-muted/40 via-background to-background">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto space-y-10">
            <div className="space-y-4 text-center max-w-3xl mx-auto">
              <Badge className="bg-indigo-50 text-indigo-600 border-indigo-200/50 w-fit mx-auto">
                Loved by top agents
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-foreground leading-tight">
                Social proof that speaks for itself
              </h2>
              <p className="text-lg text-muted-foreground">
                North Reports make it effortless to show sellers exactly how you&apos;re marketing their listing and the impact you&apos;re creating.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-10 items-start">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <img
                    src={kristiJencks}
                    alt="Kristi Jencks"
                    className="h-14 w-14 rounded-full object-cover border"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-foreground">Kristi Jencks</span>
                      <BadgeCheck className="h-4 w-4 text-indigo-600" />
                    </div>
                    <p className="text-sm text-muted-foreground">Real Estate Coach · Speaker · Team Leader</p>
                  </div>
                </div>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                  <li>Real Estate Coach for Tom Ferry</li>
                  <li>Speaker</li>
                  <li>Consultant for AI Marketing Academy</li>
                  <li>Real Estate Team Leader</li>
                  <li>Host of the Take it and Run podcast</li>
                </ul>
              </div>

              <Card className="border-2 border-indigo-300/30 shadow-xl overflow-hidden">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <img
                      src={kristiJencks}
                      alt="Kristi Jencks"
                      className="h-12 w-12 rounded-full object-cover border"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-foreground">Kristi Jencks</span>
                        <BadgeCheck className="h-4 w-4 text-indigo-600" />
                      </div>
                      <p className="text-sm text-muted-foreground">@kristijencks</p>
                    </div>
                  </div>

                  <p className="text-lg text-foreground leading-relaxed">
                    NORTH Report for Sellers is basically the car fax for listings. If agents are not using this to show their sellers what they&apos;re doing on their behalf, they&apos;re seriously missing out. <span className="text-indigo-600 font-semibold">#sellerreport</span> <span className="text-indigo-600 font-semibold">#north</span>
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <ComparisonTable />

      {/* API & Automation Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-900 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-500 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-white/20 text-white border-white/30 hover:bg-white/30">
                <Code2 className="h-3 w-3 mr-1" />
                Developer Ready
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Built for Automation
              </h2>
              <p className="text-xl text-white/80 max-w-3xl mx-auto">
                Connect North to your existing tools with our full REST API. Automate report creation, sync showings, and receive real-time notifications.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Code Preview */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-white/70 mb-2">
                  <Code2 className="h-4 w-4" />
                  <span>Simple REST API</span>
                </div>
                <Card className="bg-[#1e1e1e] border-gray-700 overflow-hidden shadow-2xl">
                  <div className="bg-[#2d2d2d] px-4 py-2 flex items-center gap-2 border-b border-gray-700">
                    <div className="flex gap-1.5">
                      <div className="h-3 w-3 rounded-full bg-red-400/60"></div>
                      <div className="h-3 w-3 rounded-full bg-yellow-400/60"></div>
                      <div className="h-3 w-3 rounded-full bg-green-400/60"></div>
                    </div>
                    <span className="text-xs text-gray-400 font-mono ml-2">create-report.sh</span>
                  </div>
                  <div className="p-4 font-mono text-sm">
                    <pre className="text-gray-300 overflow-x-auto">
                      <code>{`curl -X POST \\
  "https://api.northreports.com/v1/reports" \\
  -H "Authorization: Bearer $API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "address": "123 Main Street",
    "city": "Austin",
    "state": "TX",
    "price": 450000,
    "bedrooms": 3,
    "bathrooms": 2
  }'`}</code>
                    </pre>
                  </div>
                </Card>
                <p className="text-sm text-white/70">
                  Create reports, manage showings, and sync data with just a few lines of code.
                </p>
              </div>

              {/* Features List */}
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Zap className="h-6 w-6 text-indigo-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-1 text-white">Zapier & Make.com Ready</h4>
                    <p className="text-sm text-white/70">
                      Connect to 5,000+ apps. Automatically create reports when new listings hit your CRM.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Webhook className="h-6 w-6 text-indigo-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-1 text-white">Real-time Webhooks</h4>
                    <p className="text-sm text-white/70">
                      Get instant notifications when reports are published or showings are scheduled.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-6 w-6 text-indigo-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-1 text-white">Secure & Reliable</h4>
                    <p className="text-sm text-white/70">
                      API keys with full access control. Rate limiting and usage tracking built-in.
                    </p>
                  </div>
                </div>

                <Button className="gap-2 mt-4 bg-white text-indigo-900 hover:bg-white/90" asChild>
                  <a href="https://docs.northreports.com" target="_blank" rel="noopener noreferrer">
                    <FileText className="h-4 w-4" />
                    View API Documentation
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Spotlight Feature Section */}
      <section className="relative bg-white py-24 sm:py-32">
        <SpotlightIconPattern opacity={0.12} />
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl sm:text-center">
            <h2 className="text-base font-semibold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent inline-block">Introducing Spotlight</h2>
            <p className="mt-2 text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">Turn your North data into a shareable showcase.</p>
            <p className="mt-6 text-lg text-gray-600">Spotlight transforms your sales history, market stats, client reviews, and active listings into a polished marketing page you can share at every listing appointment.</p>
          </div>
        </div>
        <div className="relative overflow-hidden pt-16">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            {/* Live Spotlight Demo Preview */}
            <div className="mb-[-6%] w-full rounded-xl shadow-2xl ring-1 ring-gray-900/10 overflow-hidden bg-white">
              {/* Browser chrome */}
              <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-300" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-300" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-300" />
                </div>
                <div className="flex-1 mx-12">
                  <div className="bg-gray-100 rounded-full px-4 py-1 text-xs text-gray-400 text-center font-mono">
                    northreports.com/s/your-team
                  </div>
                </div>
              </div>
              <iframe
                src="/demo-spotlight"
                className="w-full border-0"
                style={{ height: '700px' }}
                title="Spotlight Demo Preview"
              />
            </div>

            {/* SVG Skeleton fallback (kept for reference) */}
            {false && <svg viewBox="0 0 1200 1300" fill="none" className="mb-[-12%] w-full rounded-xl shadow-2xl ring-1 ring-gray-900/10">
              {/* Browser chrome */}
              <rect width="1200" height="1300" rx="12" fill="white" />
              <rect width="1200" height="36" rx="12" fill="#f9fafb" />
              <rect x="0" y="34" width="1200" height="2" fill="#e5e7eb" />
              <circle cx="24" cy="18" r="5" fill="#fca5a5" />
              <circle cx="44" cy="18" r="5" fill="#fcd34d" />
              <circle cx="64" cy="18" r="5" fill="#86efac" />
              <rect x="480" y="10" width="240" height="16" rx="8" fill="#f3f4f6" />

              {/* ── Hero ── dark gradient with centered text */}
              <rect x="0" y="36" width="1200" height="250" fill="url(#spotHero)" />
              <circle cx="600" cy="100" r="20" fill="white" opacity="0.1" />
              <circle cx="600" cy="100" r="12" stroke="hsl(190,95%,45%)" strokeWidth="1.5" fill="none" opacity="0.5" />
              <rect x="400" y="140" width="400" height="26" rx="6" fill="white" opacity="0.88" />
              <rect x="440" y="185" width="320" height="10" rx="4" fill="white" opacity="0.3" />
              <rect x="480" y="208" width="240" height="10" rx="4" fill="white" opacity="0.2" />
              <rect x="560" y="245" width="80" height="4" rx="2" fill="hsl(190,95%,45%)" opacity="0.75" />

              {/* ── Speed to Sold ── light bg, 3 stat cards */}
              <rect x="0" y="286" width="1200" height="155" fill="#f8fafc" />
              <rect x="80" y="306" width="120" height="10" rx="3" fill="#475569" opacity="0.35" />
              <rect x="80" y="332" width="320" height="88" rx="12" fill="white" stroke="#e2e8f0" strokeWidth="1" />
              <rect x="112" y="352" width="80" height="26" rx="5" fill="hsl(190,95%,45%)" opacity="0.8" />
              <rect x="112" y="390" width="120" height="8" rx="3" fill="#94a3b8" opacity="0.45" />
              <rect x="440" y="332" width="320" height="88" rx="12" fill="white" stroke="#e2e8f0" strokeWidth="1" />
              <rect x="472" y="352" width="100" height="26" rx="5" fill="hsl(280,85%,70%)" opacity="0.8" />
              <rect x="472" y="390" width="120" height="8" rx="3" fill="#94a3b8" opacity="0.45" />
              <rect x="800" y="332" width="320" height="88" rx="12" fill="white" stroke="#e2e8f0" strokeWidth="1" />
              <rect x="832" y="352" width="110" height="26" rx="5" fill="hsl(270,85%,65%)" opacity="0.8" />
              <rect x="832" y="390" width="120" height="8" rx="3" fill="#94a3b8" opacity="0.45" />

              {/* ── Digital Footprint ── white bg, portal platform bars */}
              <rect x="0" y="441" width="1200" height="230" fill="white" />
              <rect x="80" y="468" width="140" height="10" rx="3" fill="#475569" opacity="0.35" />
              <rect x="80" y="488" width="220" height="7" rx="2" fill="#94a3b8" opacity="0.2" />
              {/* Zillow */}
              <rect x="80" y="520" width="60" height="7" rx="2" fill="#94a3b8" opacity="0.35" />
              <rect x="175" y="515" width="630" height="16" rx="6" fill="#1277e1" opacity="0.55" />
              <rect x="818" y="520" width="50" height="7" rx="2" fill="#475569" opacity="0.25" />
              {/* Realtor.com */}
              <rect x="80" y="552" width="80" height="7" rx="2" fill="#94a3b8" opacity="0.35" />
              <rect x="175" y="547" width="470" height="16" rx="6" fill="#d92228" opacity="0.45" />
              <rect x="658" y="552" width="50" height="7" rx="2" fill="#475569" opacity="0.25" />
              {/* Homes.com */}
              <rect x="80" y="584" width="70" height="7" rx="2" fill="#94a3b8" opacity="0.35" />
              <rect x="175" y="579" width="350" height="16" rx="6" fill="hsl(190,95%,45%)" opacity="0.45" />
              <rect x="538" y="584" width="50" height="7" rx="2" fill="#475569" opacity="0.25" />
              {/* Redfin */}
              <rect x="80" y="616" width="52" height="7" rx="2" fill="#94a3b8" opacity="0.35" />
              <rect x="175" y="611" width="270" height="16" rx="6" fill="#a02b2d" opacity="0.45" />
              <rect x="458" y="616" width="50" height="7" rx="2" fill="#475569" opacity="0.25" />
              {/* Trulia */}
              <rect x="80" y="648" width="46" height="7" rx="2" fill="#94a3b8" opacity="0.35" />
              <rect x="175" y="643" width="210" height="16" rx="6" fill="#54b946" opacity="0.45" />
              <rect x="398" y="648" width="50" height="7" rx="2" fill="#475569" opacity="0.25" />

              {/* ── Market Comparison ── dark bg, bar charts + demand gauge */}
              <rect x="0" y="671" width="1200" height="200" fill="url(#spotMarket)" />
              <rect x="80" y="698" width="155" height="10" rx="3" fill="white" opacity="0.45" />
              {/* Days on Market - Your team */}
              <rect x="80" y="730" width="55" height="6" rx="2" fill="white" opacity="0.2" />
              <rect x="175" y="725" width="310" height="15" rx="6" fill="hsl(190,95%,45%)" opacity="0.65" />
              {/* Days on Market - Market avg */}
              <rect x="80" y="756" width="55" height="6" rx="2" fill="white" opacity="0.2" />
              <rect x="175" y="751" width="490" height="15" rx="6" fill="white" opacity="0.12" />
              {/* Sale to List - Your team */}
              <rect x="80" y="792" width="55" height="6" rx="2" fill="white" opacity="0.2" />
              <rect x="175" y="787" width="430" height="15" rx="6" fill="hsl(280,85%,70%)" opacity="0.65" />
              {/* Sale to List - Market avg */}
              <rect x="80" y="818" width="55" height="6" rx="2" fill="white" opacity="0.2" />
              <rect x="175" y="813" width="350" height="15" rx="6" fill="white" opacity="0.12" />
              {/* Demand gauge circle */}
              <circle cx="1010" cy="775" r="52" stroke="white" strokeWidth="3" fill="none" opacity="0.08" />
              <circle cx="1010" cy="775" r="52" stroke="hsl(190,95%,45%)" strokeWidth="3.5" fill="none" opacity="0.55" strokeDasharray="245 82" strokeLinecap="round" transform="rotate(-90 1010 775)" />
              <rect x="984" y="764" width="52" height="18" rx="4" fill="hsl(190,95%,45%)" opacity="0.75" />
              <rect x="990" y="790" width="40" height="6" rx="2" fill="white" opacity="0.25" />

              {/* ── Homes Carousel ── light bg, listing photo cards */}
              <rect x="0" y="871" width="1200" height="230" fill="#fafafa" />
              <rect x="80" y="895" width="100" height="10" rx="3" fill="#475569" opacity="0.35" />
              {/* Card 1 */}
              <rect x="80" y="922" width="175" height="155" rx="10" fill="white" stroke="#e2e8f0" strokeWidth="1" />
              <rect x="81" y="923" width="173" height="95" rx="9" fill="#e2e8f0" />
              <rect x="95" y="1030" width="105" height="8" rx="3" fill="#475569" opacity="0.45" />
              <rect x="95" y="1048" width="60" height="7" rx="2" fill="hsl(190,95%,45%)" opacity="0.45" />
              {/* Card 2 */}
              <rect x="278" y="922" width="175" height="155" rx="10" fill="white" stroke="#e2e8f0" strokeWidth="1" />
              <rect x="279" y="923" width="173" height="95" rx="9" fill="#e2e8f0" />
              <rect x="293" y="1030" width="105" height="8" rx="3" fill="#475569" opacity="0.45" />
              <rect x="293" y="1048" width="60" height="7" rx="2" fill="hsl(280,85%,70%)" opacity="0.45" />
              {/* Card 3 */}
              <rect x="476" y="922" width="175" height="155" rx="10" fill="white" stroke="#e2e8f0" strokeWidth="1" />
              <rect x="477" y="923" width="173" height="95" rx="9" fill="#e2e8f0" />
              <rect x="491" y="1030" width="105" height="8" rx="3" fill="#475569" opacity="0.45" />
              <rect x="491" y="1048" width="60" height="7" rx="2" fill="hsl(270,85%,65%)" opacity="0.45" />
              {/* Card 4 */}
              <rect x="674" y="922" width="175" height="155" rx="10" fill="white" stroke="#e2e8f0" strokeWidth="1" />
              <rect x="675" y="923" width="173" height="95" rx="9" fill="#e2e8f0" />
              <rect x="689" y="1030" width="105" height="8" rx="3" fill="#475569" opacity="0.45" />
              <rect x="689" y="1048" width="60" height="7" rx="2" fill="hsl(190,95%,45%)" opacity="0.45" />
              {/* Card 5 (partial) */}
              <rect x="872" y="922" width="175" height="155" rx="10" fill="white" stroke="#e2e8f0" strokeWidth="1" />
              <rect x="873" y="923" width="173" height="95" rx="9" fill="#e2e8f0" />
              {/* Card 6 (edge peek) */}
              <rect x="1070" y="922" width="175" height="155" rx="10" fill="white" stroke="#e2e8f0" strokeWidth="0.5" opacity="0.5" />
              <rect x="1071" y="923" width="173" height="95" rx="9" fill="#e2e8f0" opacity="0.5" />

              {/* ── Coverage Map ── dark bg with pins */}
              <rect x="0" y="1101" width="1200" height="199" fill="url(#spotMap)" />
              <rect x="80" y="1125" width="115" height="10" rx="3" fill="white" opacity="0.35" />
              <circle cx="180" cy="1185" r="7" fill="hsl(190,95%,45%)" opacity="0.65" />
              <circle cx="310" cy="1220" r="5" fill="hsl(280,85%,70%)" opacity="0.55" />
              <circle cx="460" cy="1170" r="8" fill="hsl(190,95%,45%)" opacity="0.75" />
              <circle cx="560" cy="1210" r="5.5" fill="hsl(270,85%,65%)" opacity="0.5" />
              <circle cx="700" cy="1180" r="7" fill="hsl(280,85%,70%)" opacity="0.65" />
              <circle cx="830" cy="1205" r="6" fill="hsl(190,95%,45%)" opacity="0.55" />
              <circle cx="940" cy="1165" r="5.5" fill="hsl(270,85%,65%)" opacity="0.5" />
              <circle cx="1060" cy="1195" r="6.5" fill="hsl(190,95%,45%)" opacity="0.6" />

              <defs>
                <linearGradient id="spotHero" x1="0" y1="36" x2="1200" y2="286">
                  <stop stopColor="hsl(270,50%,15%)" />
                  <stop offset="1" stopColor="hsl(270,40%,25%)" />
                </linearGradient>
                <linearGradient id="spotMarket" x1="0" y1="671" x2="1200" y2="871">
                  <stop stopColor="hsl(220,40%,12%)" />
                  <stop offset="1" stopColor="hsl(260,45%,16%)" />
                </linearGradient>
                <linearGradient id="spotMap" x1="0" y1="1101" x2="0" y2="1300">
                  <stop stopColor="hsl(210,25%,15%)" />
                  <stop offset="1" stopColor="hsl(210,30%,20%)" />
                </linearGradient>
              </defs>
            </svg>}
            <div aria-hidden="true" className="relative">
              <div className="absolute -inset-x-20 bottom-0 bg-gradient-to-t from-white pt-[7%]" />
            </div>
          </div>
        </div>
        <div className="mx-auto mt-16 max-w-7xl px-6 sm:mt-20 md:mt-24 lg:px-8">
          <dl className="mx-auto grid max-w-2xl grid-cols-1 gap-x-6 gap-y-10 text-base text-gray-600 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-x-8 lg:gap-y-16">
            <div className="relative pl-9">
              <dt className="inline font-semibold text-gray-900">
                <TrendingUp className="absolute top-1 left-1 h-5 w-5 text-purple-600" />
                Proven Track Record.
              </dt>
              <dd className="inline"> Animated career stats from your North reports — homes sold, average sale price, and price range.</dd>
            </div>
            <div className="relative pl-9">
              <dt className="inline font-semibold text-gray-900">
                <BarChart3 className="absolute top-1 left-1 h-5 w-5 text-indigo-600" />
                Digital Footprint.
              </dt>
              <dd className="inline"> Per-platform breakdown of portal views and saves across Zillow, Realtor.com, Homes.com, Redfin, and Trulia.</dd>
            </div>
            <div className="relative pl-9">
              <dt className="inline font-semibold text-gray-900">
                <BarChart className="absolute top-1 left-1 h-5 w-5 text-pink-500" />
                Market Comparison.
              </dt>
              <dd className="inline"> Side-by-side days-on-market comparison against local averages with a demand rating.</dd>
            </div>
            <div className="relative pl-9">
              <dt className="inline font-semibold text-gray-900">
                <Home className="absolute top-1 left-1 h-5 w-5 text-indigo-600" />
                Listings Showcase.
              </dt>
              <dd className="inline"> Scrollable photo cards of your active listings with price, details, and portal view counts.</dd>
            </div>
            <div className="relative pl-9">
              <dt className="inline font-semibold text-gray-900">
                <Share2 className="absolute top-1 left-1 h-5 w-5 text-purple-600" />
                Coverage Map.
              </dt>
              <dd className="inline"> Interactive map pinning your sold homes to show geographic reach at a glance.</dd>
            </div>
            <div className="relative pl-9">
              <dt className="inline font-semibold text-gray-900">
                <BadgeCheck className="absolute top-1 left-1 h-5 w-5 text-pink-500" />
                Client Reviews.
              </dt>
              <dd className="inline"> Verified Zillow reviews with an AI-generated summary so sellers see your reputation instantly.</dd>
            </div>
          </dl>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="text-base font-semibold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent inline-block">Pricing</h2>
            <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              Simple, Scalable Pricing
            </p>
          </div>
          <p className="mx-auto mt-6 max-w-2xl text-center text-lg font-medium text-gray-600">
            Start with a 14-day free trial and scale as your listings grow. No hidden fees or surprises.
          </p>

          <div className="mt-8 flex justify-center">
            <Badge className="bg-indigo-50 text-indigo-600 border border-indigo-200/50 hover:bg-indigo-100 px-6 py-2 text-sm font-medium">
              14-Day Free Trial on All Plans
            </Badge>
          </div>

          <div className="mt-10 flex flex-col items-center gap-3">
            <div className="grid grid-cols-2 gap-x-1 rounded-full p-1 text-center text-xs font-semibold ring-1 ring-inset ring-gray-200">
              <button
                onClick={() => setBillingFrequency('monthly')}
                className={`rounded-full px-5 py-1.5 transition-colors ${
                  billingFrequency === 'monthly'
                    ? 'bg-gradient-to-br from-indigo-600 to-purple-600 text-white'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingFrequency('annual')}
                className={`rounded-full px-5 py-1.5 transition-colors ${
                  billingFrequency === 'annual'
                    ? 'bg-gradient-to-br from-indigo-600 to-purple-600 text-white'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                Annual
              </button>
            </div>
            {billingFrequency === 'annual' && (
              <div className="flex items-center gap-2 animate-in fade-in duration-300">
                <Badge className="bg-green-600 hover:bg-green-600 text-white px-3 py-1">1 Month Free</Badge>
                <span className="text-sm font-medium text-green-700">Save up to $249/year</span>
              </div>
            )}
          </div>

          <div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 md:max-w-2xl md:grid-cols-2 lg:max-w-4xl xl:mx-0 xl:max-w-none xl:grid-cols-4">
            {/* Starter */}
            <div className="rounded-3xl p-8 ring-1 ring-gray-200 hover:ring-gray-300 transition-all">
              <div className="flex items-center justify-between gap-x-4">
                <h3 className="text-lg font-semibold text-gray-900">North Starter</h3>
              </div>
              <p className="mt-4 text-sm text-gray-600">Great for solo agents or newer listing specialists who handle a few listings a year.</p>
              <p className="mt-6 flex items-baseline gap-x-1">
                <span className="text-4xl font-semibold tracking-tight text-gray-900">
                  ${billingFrequency === 'annual' ? planPricing.starter.annual : planPricing.starter.monthly}
                </span>
                <span className="text-sm font-semibold text-gray-600">/{billingFrequency === 'annual' ? 'year' : 'month'}</span>
              </p>
              {billingFrequency === 'annual' && (
                <p className="mt-1 text-xs text-gray-500">${Math.round(planPricing.starter.annual / 12)}/month, billed annually</p>
              )}
              <Link
                to={isAuthenticated ? "/settings?tab=billing" : `/signup?plan=starter&billing=${billingFrequency}`}
                className="mt-6 block w-full rounded-md px-3 py-2 text-center text-sm font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:ring-indigo-300 transition-colors"
              >
                Continue with Starter
              </Link>
              <ul className="mt-8 space-y-3 text-sm text-gray-600">
                <li className="flex gap-x-3">
                  <Check className="h-6 w-5 flex-none text-indigo-600" />
                  3 active reports
                </li>
                <li className="flex gap-x-3">
                  <Check className="h-6 w-5 flex-none text-purple-600" />
                  Spotlight team showcase page
                </li>
              </ul>
            </div>

            {/* Lite */}
            <div className="rounded-3xl p-8 ring-1 ring-gray-200 hover:ring-gray-300 transition-all">
              <div className="flex items-center justify-between gap-x-4">
                <h3 className="text-lg font-semibold text-gray-900">North Lite</h3>
              </div>
              <p className="mt-4 text-sm text-gray-600">Ideal for agents with consistent listing activity who keep multiple sellers informed.</p>
              <p className="mt-6 flex items-baseline gap-x-1">
                <span className="text-4xl font-semibold tracking-tight text-gray-900">
                  ${billingFrequency === 'annual' ? planPricing.lite.annual : planPricing.lite.monthly}
                </span>
                <span className="text-sm font-semibold text-gray-600">/{billingFrequency === 'annual' ? 'year' : 'month'}</span>
              </p>
              {billingFrequency === 'annual' && (
                <p className="mt-1 text-xs text-gray-500">${Math.round(planPricing.lite.annual / 12)}/month, billed annually</p>
              )}
              <Link
                to={isAuthenticated ? "/settings?tab=billing" : `/signup?plan=lite&billing=${billingFrequency}`}
                className="mt-6 block w-full rounded-md px-3 py-2 text-center text-sm font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:ring-indigo-300 transition-colors"
              >
                Continue with Lite
              </Link>
              <ul className="mt-8 space-y-3 text-sm text-gray-600">
                <li className="flex gap-x-3">
                  <Check className="h-6 w-5 flex-none text-indigo-600" />
                  12 active reports
                </li>
                <li className="flex gap-x-3">
                  <Check className="h-6 w-5 flex-none text-purple-600" />
                  Spotlight team showcase page
                </li>
              </ul>
            </div>

            {/* Growth - Featured */}
            <div className="relative rounded-3xl p-8 ring-2 ring-indigo-500 hover:ring-indigo-400 transition-all">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <Badge className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 text-white hover:from-indigo-500 shadow-lg px-4 py-1 text-xs font-semibold">
                  Most popular
                </Badge>
              </div>
              <div className="flex items-center justify-between gap-x-4">
                <h3 className="text-lg font-semibold text-indigo-600">North Growth</h3>
              </div>
              <p className="mt-4 text-sm text-gray-600">A strong fit for established listing agents and small teams managing higher volume.</p>
              <p className="mt-6 flex items-baseline gap-x-1">
                <span className="text-4xl font-semibold tracking-tight text-gray-900">
                  ${billingFrequency === 'annual' ? planPricing.growth.annual : planPricing.growth.monthly}
                </span>
                <span className="text-sm font-semibold text-gray-600">/{billingFrequency === 'annual' ? 'year' : 'month'}</span>
              </p>
              {billingFrequency === 'annual' && (
                <p className="mt-1 text-xs text-gray-500">${Math.round(planPricing.growth.annual / 12)}/month, billed annually</p>
              )}
              <Link
                to={isAuthenticated ? "/settings?tab=billing" : `/signup?plan=growth&billing=${billingFrequency}`}
                className="mt-6 block w-full rounded-md bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 hover:from-indigo-500 hover:via-purple-500 hover:to-pink-400 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm transition-colors"
              >
                Continue with Growth
              </Link>
              <ul className="mt-8 space-y-3 text-sm text-gray-600">
                <li className="flex gap-x-3">
                  <Check className="h-6 w-5 flex-none text-indigo-600" />
                  25 active reports
                </li>
                <li className="flex gap-x-3">
                  <Check className="h-6 w-5 flex-none text-purple-600" />
                  Spotlight team showcase page
                </li>
              </ul>
            </div>

            {/* Scale */}
            <div className="rounded-3xl p-8 ring-1 ring-gray-200 hover:ring-gray-300 transition-all">
              <div className="flex items-center justify-between gap-x-4">
                <h3 className="text-lg font-semibold text-gray-900">North Scale</h3>
              </div>
              <p className="mt-4 text-sm text-gray-600">Built for top producers, high-volume listing teams, and brokerages running at scale.</p>
              <p className="mt-6 flex items-baseline gap-x-1">
                <span className="text-4xl font-semibold tracking-tight text-gray-900">
                  ${billingFrequency === 'annual' ? planPricing.scale.annual : planPricing.scale.monthly}
                </span>
                <span className="text-sm font-semibold text-gray-600">/{billingFrequency === 'annual' ? 'year' : 'month'}</span>
              </p>
              {billingFrequency === 'annual' && (
                <p className="mt-1 text-xs text-gray-500">${Math.round(planPricing.scale.annual / 12)}/month, billed annually</p>
              )}
              <Link
                to={isAuthenticated ? "/settings?tab=billing" : `/signup?plan=scale&billing=${billingFrequency}`}
                className="mt-6 block w-full rounded-md px-3 py-2 text-center text-sm font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:ring-indigo-300 transition-colors"
              >
                Continue with Scale
              </Link>
              <ul className="mt-8 space-y-3 text-sm text-gray-600">
                <li className="flex gap-x-3">
                  <Check className="h-6 w-5 flex-none text-indigo-600" />
                  40 active reports
                </li>
                <li className="flex gap-x-3">
                  <Check className="h-6 w-5 flex-none text-purple-600" />
                  Spotlight team showcase page
                </li>
              </ul>
            </div>
          </div>

          {/* Custom Pricing */}
          <div className="mt-16 mx-auto max-w-3xl rounded-3xl p-8 ring-1 ring-gray-200 sm:p-10 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Need more than 40 active listings?</h3>
                <p className="mt-1 text-sm text-gray-600">We'll build a custom plan that fits your needs</p>
              </div>
              <a
                href="mailto:sales@northreports.com"
                className="inline-flex items-center rounded-md bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 hover:from-indigo-500 hover:via-purple-500 hover:to-pink-400 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors"
              >
                Contact Sales
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-card">
        <div className="container mx-auto px-6 py-8">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Logo customLogoUrl={logoUrl} size="sm" />
              <span className="text-sm text-foreground">
                © {new Date().getFullYear()} North Reports. All rights reserved.
              </span>
            </div>
            <div className="flex flex-wrap justify-center gap-6">
              <Link to="/login" className="text-sm text-foreground hover:text-indigo-600 transition-colors">
                Sign In
              </Link>
              <Link to="/signup" className="text-sm text-foreground hover:text-indigo-600 transition-colors">
                Sign Up
              </Link>
              <a href="https://docs.northreports.com" target="_blank" rel="noopener noreferrer" className="text-sm text-foreground hover:text-indigo-600 transition-colors">
                API Docs
              </a>
              <Link to="/privacy-policy" className="text-sm text-foreground hover:text-indigo-600 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="text-sm text-foreground hover:text-indigo-600 transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
